import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { FedexRatingResponseDto } from "@/types/api/api-types";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import ExpressIcon from "@/assets/icons/express.svg?react";

export const PRIORITY_SERVICE_TYPES = [
  "INTERNATIONAL_PRIORITY",
  "INTERNATIONAL_FIRST",
  "EUROPE_FIRST_INTERNATIONAL_PRIORITY",
  "FEDEX_INTERNATIONAL_PRIORITY",
  "INTERNATIONAL_PRIORITY_FREIGHT",
  "FEDEX_INTERNATIONAL_PRIORITY",
  "FEDEX_INTERNATIONAL_PRIORITY_EXPRESS",
  "FEDEX_PRIORITY",
  "FEDEX_PRIORITY_EXPRESS",
];

export const SERVICE_TYPE_DISPLAY_ORDER: string[] = [
  // International
  "INTERNATIONAL_ECONOMY",
  "FEDEX_REGIONAL_ECONOMY",
  "FEDEX_INTERNATIONAL_PRIORITY",
  "INTERNATIONAL_PRIORITY",
  "FEDEX_INTERNATIONAL_PRIORITY_EXPRESS",
  "FEDEX_INTERNATIONAL_CONNECT_PLUS",
  "INTERNATIONAL_FIRST",
  "EUROPE_FIRST_INTERNATIONAL_PRIORITY",
  "INTERNATIONAL_ECONOMY_FREIGHT",
  "FEDEX_REGIONAL_ECONOMY_FREIGHT",
  "FEDEX_INTERNATIONAL_DEFERRED_FREIGHT",
  "INTERNATIONAL_PRIORITY_FREIGHT",
  // Intra-country (EU domestic)
  "FEDEX_ECONOMY",
  "FEDEX_PRIORITY",
  "FEDEX_PRIORITY_EXPRESS",
  "FEDEX_FIRST",
  "FEDEX_PRIORITY_FREIGHT",
  "FEDEX_PRIORITY_EXPRESS_FREIGHT",
  // US domestic
  "FEDEX_GROUND",
  "GROUND_HOME_DELIVERY",
  "FEDEX_EXPRESS_SAVER",
  "FEDEX_2_DAY",
  "STANDARD_OVERNIGHT",
  "PRIORITY_OVERNIGHT",
  "FIRST_OVERNIGHT",
  "FEDEX_3_DAY_FREIGHT",
  "FEDEX_2_DAY_FREIGHT",
  "FEDEX_1_DAY_FREIGHT",
  "FEDEX_DISTANCE_DEFERRED",
  "FEDEX_NEXT_DAY_END_OF_DAY",
  "FEDEX_NEXT_DAY_AFTERNOON",
  "FEDEX_NEXT_DAY_MID_MORNING",
  "FEDEX_NEXT_DAY_EARLY_MORNING",
  "SAME_DAY_CITY",
];

const getServiceTypeRank = (serviceType: string) => {
  const index = SERVICE_TYPE_DISPLAY_ORDER.indexOf(serviceType);
  return index === -1 ? Number.POSITIVE_INFINITY : index;
};

export type RatingSelection = {
  serviceType: string;
  serviceName: string;
  accountId: string;
  accountName: string;
};

type RatingResultCardProps = {
  data: FedexRatingResponseDto;
  selection?: RatingSelection | null;
  onSelect?: (selection: RatingSelection | null) => void;
  readOnly?: boolean;
};

export type AccountRate = {
  accountName: string;
  accountId: string;
  accountNumber: string;
  serviceType: string;
  serviceName: string;
  rateType: string;
  totalBaseCharge: number;
  totalNetCharge: number;
  totalVatCharge: number;
  totalDutiesAndTaxes: number;
  totalSurcharges: number;
  totalFreightDiscounts: number;
  currency: string;
  surcharges: { type: string; description: string; amount: number }[];
  transitTime?: string;
  deliveryDate?: string;
  saturdayDelivery: boolean;
};

type ServiceGroup = {
  serviceType: string;
  serviceName: string;
  transitTime?: string;
  accounts: AccountRate[];
  bestNetCharge: number;
};

const formatPrice = (value: number, currency = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(value);

const ResultRow = ({
  label,
  value,
  currency,
}: {
  label: string;
  value?: number;
  currency?: string;
}) => {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-sm font-medium">{formatPrice(value, currency)}</span>
    </div>
  );
};

const DetailToggle = ({ open, onToggle }: { open: boolean; onToggle: () => void }) => {
  const { t } = useTranslation("boRatings");
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
    >
      <ChevronDown
        className={cn("h-3.5 w-3.5 transition-transform duration-200", open && "rotate-180")}
      />
      {open ? t("resultCard.hideDetail") : t("resultCard.showDetail")}
    </button>
  );
};

function useServiceGroups(data: FedexRatingResponseDto) {
  return useMemo(() => {
    const map = new Map<string, ServiceGroup>();
    let earliestDeliveryDate: string | undefined;

    for (const accountRating of data.accountRatings) {
      for (const rate of accountRating.rates) {
        if (rate.saturdayDelivery) continue;

        const entry: AccountRate = {
          accountName: accountRating.account.accountName,
          accountId: accountRating.account.accountId,
          accountNumber: accountRating.account.accountNumber,
          serviceType: rate.serviceType,
          serviceName: rate.serviceName,
          rateType: rate.rateType,
          totalBaseCharge: rate.totalBaseCharge,
          totalNetCharge: rate.totalNetCharge,
          totalVatCharge: rate.totalVatCharge,
          totalDutiesAndTaxes: rate.totalDutiesAndTaxes,
          totalSurcharges: rate.totalSurcharges,
          totalFreightDiscounts: rate.totalFreightDiscounts,
          currency: rate.currency,
          surcharges: rate.surcharges,
          transitTime: rate.transitTime,
          deliveryDate: rate.deliveryDate,
          saturdayDelivery: rate.saturdayDelivery,
        };

        if (
          rate.deliveryDate &&
          (!earliestDeliveryDate || rate.deliveryDate < earliestDeliveryDate)
        ) {
          earliestDeliveryDate = rate.deliveryDate;
        }

        const existing = map.get(rate.serviceType);
        if (existing) {
          existing.accounts.push(entry);
          existing.bestNetCharge = Math.min(existing.bestNetCharge, rate.totalNetCharge);
        } else {
          map.set(rate.serviceType, {
            serviceType: rate.serviceType,
            serviceName: rate.serviceName,
            transitTime: rate.transitTime,
            accounts: [entry],
            bestNetCharge: rate.totalNetCharge,
          });
        }
      }
    }

    const groups = Array.from(map.values()).sort(
      (a, b) =>
        getServiceTypeRank(a.serviceType) - getServiceTypeRank(b.serviceType) ||
        a.bestNetCharge - b.bestNetCharge,
    );
    return { groups, earliestDeliveryDate };
  }, [data]);
}

export const AccountCard = ({
  rate,
  isBest,
  isFastest,
  isSelected,
  detailOpen,
  onDetailToggle,
  onToggle,
  readOnly = false,
}: {
  rate: AccountRate;
  isBest: boolean;
  isFastest: boolean;
  isSelected: boolean;
  detailOpen: boolean;
  onDetailToggle: () => void;
  onToggle: () => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation("boRatings");
  return (
    <div
      className={cn(
        "relative flex flex-1 flex-col rounded-lg border-2 p-3 transition-colors",
        !readOnly && isSelected
          ? "border-primary bg-primary/5 ring-primary/20 ring-1"
          : isBest
            ? "border-green-300 bg-green-50/50"
            : "border-gray-200 bg-gray-50/50",
      )}
    >
      {!readOnly && (
        <div className="absolute top-2.5 right-2.5">
          <Checkbox checked={isSelected} onCheckedChange={onToggle} />
        </div>
      )}

      <div className={cn("mb-1 flex flex-wrap items-center gap-1.5", !readOnly && "pr-6")}>
        {isBest && (readOnly || !isSelected) && (
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-green-600 uppercase">
            {t("resultCard.bestPrice")}
          </span>
        )}
        {isFastest && (readOnly || !isSelected) && (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-blue-600 uppercase">
            {t("resultCard.fastest")}
          </span>
        )}
        {!readOnly && isSelected && (
          <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wider uppercase">
            {t("resultCard.selected")}
          </span>
        )}
      </div>

      <div className={cn("flex items-baseline justify-between gap-2", !readOnly && "pr-6")}>
        <span
          className={cn(
            "text-lg font-bold",
            !readOnly && isSelected ? "text-primary" : isBest ? "text-green-800" : "text-gray-900",
          )}
        >
          {formatPrice(rate.totalNetCharge, rate.currency)}
        </span>
        <DetailToggle open={detailOpen} onToggle={onDetailToggle} />
      </div>

      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          detailOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-2 flex flex-col pt-1">
            <ResultRow
              label={t("resultCard.baseCharge")}
              value={rate.totalBaseCharge}
              currency={rate.currency}
            />
            {rate.surcharges.map((surcharge, i) => (
              <ResultRow
                key={`${surcharge.type}-${i}`}
                label={surcharge.description}
                value={surcharge.amount}
                currency={rate.currency}
              />
            ))}
            <ResultRow
              label={t("resultCard.discounts")}
              value={rate.totalFreightDiscounts ? -rate.totalFreightDiscounts : undefined}
              currency={rate.currency}
            />
            <ResultRow
              label={t("resultCard.vat")}
              value={rate.totalVatCharge}
              currency={rate.currency}
            />
            <ResultRow
              label={t("resultCard.dutiesAndTaxes")}
              value={rate.totalDutiesAndTaxes}
              currency={rate.currency}
            />
          </div>
        </div>
      </div>

      {rate.deliveryDate && (
        <p className="mt-2 pt-1 text-xs text-gray-500">
          <Trans
            t={t}
            i18nKey="resultCard.estimatedDelivery"
            values={{
              date: new Date(rate.deliveryDate).toLocaleString("fr-FR", {
                dateStyle: "long",
                timeStyle: "short",
              }),
            }}
            components={{ bold: <span className="font-bold" /> }}
          />
        </p>
      )}
    </div>
  );
};

const ServiceGroupCard = ({
  group,
  multipleAccounts,
  earliestDeliveryDate,
  selection,
  onSelect,
  readOnly = false,
}: {
  group: ServiceGroup;
  multipleAccounts: boolean;
  earliestDeliveryDate?: string;
  selection?: RatingSelection | null;
  onSelect?: (selection: RatingSelection | null) => void;
  readOnly?: boolean;
}) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const accountColumns = useMemo(() => {
    const map = new Map<string, AccountRate[]>();
    for (const rate of group.accounts) {
      const existing = map.get(rate.accountId);
      if (existing) existing.push(rate);
      else map.set(rate.accountId, [rate]);
    }
    return Array.from(map.entries());
  }, [group.accounts]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-4">
        <span className="text-lg font-semibold text-gray-900">{group.serviceName}</span>
        {PRIORITY_SERVICE_TYPES.includes(group.serviceType) && (
          <div className={`bg-brand600 flex items-center justify-center rounded-full px-2 py-1.5`}>
            <ExpressIcon />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @3xl:grid-cols-3">
        {accountColumns.map(([accountId, rates]) => (
          <div key={accountId} className="flex flex-col gap-2">
            <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
              {rates[0].accountName} ({rates[0].accountNumber.slice(-3)})
            </span>
            {rates.map((rate) => {
              const isSelected =
                selection?.serviceType === rate.serviceType &&
                selection?.accountId === rate.accountId;

              return (
                <AccountCard
                  key={`${rate.accountId}-${rate.rateType}`}
                  rate={rate}
                  isBest={multipleAccounts && rate.totalNetCharge === group.bestNetCharge}
                  isFastest={!!rate.deliveryDate && rate.deliveryDate === earliestDeliveryDate}
                  isSelected={isSelected}
                  detailOpen={detailOpen}
                  onDetailToggle={() => setDetailOpen((v) => !v)}
                  onToggle={() => {
                    if (readOnly || !onSelect) return;
                    if (isSelected) {
                      onSelect(null);
                    } else {
                      onSelect({
                        serviceType: rate.serviceType,
                        serviceName: rate.serviceName,
                        accountId: rate.accountId,
                        accountName: rate.accountName,
                      });
                    }
                  }}
                  readOnly={readOnly}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const FedexResult = ({
  data,
  selection,
  onSelect,
  readOnly = false,
}: {
  data: FedexRatingResponseDto;
  selection?: RatingSelection | null;
  onSelect?: (selection: RatingSelection | null) => void;
  readOnly?: boolean;
}) => {
  const { groups, earliestDeliveryDate } = useServiceGroups(data);
  const multipleAccounts = data.accountRatings.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <ServiceGroupCard
          key={group.serviceType}
          group={group}
          multipleAccounts={multipleAccounts}
          earliestDeliveryDate={earliestDeliveryDate}
          selection={selection}
          onSelect={onSelect}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

export const RatingResultCard = (props: RatingResultCardProps) => {
  return (
    <FedexResult
      data={props.data}
      selection={props.selection}
      onSelect={props.onSelect}
      readOnly={props.readOnly}
    />
  );
};
